/**
 * @file ConfirmationCode.jsx
 * @description Handles verification code confirmation and validation
 *
 * @section Responsibilities
 * - Manage code verification flow
 * - Handle code validation
 * - Process confirmation
 * - Manage verification state
 * - Handle verification errors
 * - Provide verification status
 *
 * @section Dependencies
 * - VerificationService: Code verification
 * - ValidationService: Code validation
 * - AuthService: Authentication handling
 * - StateManagement: Verification state
 *
 * @section Integration Points
 * - Verification API: Code validation
 * - Auth System: Authentication
 * - Validation System: Code rules
 * - Navigation System: Post-verification routing
 *
 * @section State Management
 * - verificationState: Verification status
 * - validationState: Code validation
 * - authState: Authentication status
 * - errorState: Error handling
 *
 * @section Events
 * - onCodeSubmit: Code submission
 * - onValidation: Code validation
 * - onVerification: Verification process
 * - onError: Error handling
 *
 * @section Props
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} validationRules - Code rules
 * @prop {Boolean} autoValidate - Controls validation
 * @prop {Number} codeLength - Expected code length
 */

import axios from "axios";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/confirmation-code.css";
import { useState } from "react";
import BallsBackground from "../../components/BallsBackground";
import DgButton from "../../components/DgButton";
import { useNavigate } from "react-router-dom";
import { validateOnboarding } from "../../utils/login";
import Request from "../../utils/fetchUtils";
import Swal from "sweetalert2";

const ConfirmationCode = () => {
  const navigate = useNavigate();
  const [input1, setInput1] = useState("");
  const [input2, setInput2] = useState("");
  const [input3, setInput3] = useState("");
  const [input4, setInput4] = useState("");
  const [input5, setInput5] = useState("");
  const [input6, setInput6] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const credentials = JSON.parse(sessionStorage.credentials);
  const { email } = credentials;

  const submit = () => {
    setLoading(true);
    axios
      .patch(process.env.REACT_APP_BASE_URL + "/user", {
        role: "customer",
        email: email,
        confirmationCode: input1 + input2 + input3 + input4 + input5 + input6,
      })
      .then(async (res) => {
        const resLogin = await Request(
          "/user/auth",
          {
            ...credentials,
            username: credentials.email.toLowerCase(),
          },
          "POST"
        );
        setLoading(false);
        if (resLogin.ok) {
          const { userData, token } = resLogin.data;
          localStorage.setItem("jwt", token);
          localStorage.setItem("session", JSON.stringify(userData));
          Object.keys(userData).forEach((k) => {
            const value = userData[k];
            const parsedValue =
              typeof value === "object" ? JSON.stringify(value) : value;
            localStorage.setItem(k, parsedValue);
          });
          validateOnboarding(userData, navigate);
        } else {
          setError(resLogin.message);
        }
      })
      .catch((error) => {
        setLoading(false);
        setError(error.response.data.message);
      });
  };

  const setNext = (value, index) => {
    if (!value) return;
    updateInput(value, index);
    document.getElementById(index + 1)?.focus();
  };

  const goBack = (e, index) => {
    if (e.key === "Backspace") {
      if (e.target.value) {
        updateInput("", index);
      } else {
        updateInput("", index - 1);
      }
      document.getElementById(index - 1)?.focus();
    } else if (e.key === "ArrowLeft") {
      document.getElementById(index - 1)?.focus();
    } else if (e.key === "ArrowRight") {
      document.getElementById(index + 1)?.focus();
    }
  };

  const updateInput = (value, index) => {
    switch (index) {
      case 1:
        if (value && input1.length) return;
        setInput1(value);
        break;
      case 2:
        if (value && input2.length) return;
        setInput2(value);
        break;
      case 3:
        if (value && input3.length) return;
        setInput3(value);
        break;
      case 4:
        if (value && input4.length) return;
        setInput4(value);
        break;
      case 5:
        if (value && input5.length) return;
        setInput5(value);
        break;
      case 6:
        if (value && input6.length) return;
        setInput6(value);
        break;
    }
  };

  // Nueva función para manejar el pegado del código
  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Elimina espacios y otros caracteres no numéricos
    const cleanPastedData = pastedData.replace(/\D/g, "");

    if (/^\d+$/.test(cleanPastedData)) {
      const codeArray = cleanPastedData.split("").slice(0, 6);

      if (codeArray[0]) setInput1(codeArray[0]);
      if (codeArray[1]) setInput2(codeArray[1]);
      if (codeArray[2]) setInput3(codeArray[2]);
      if (codeArray[3]) setInput4(codeArray[3]);
      if (codeArray[4]) setInput5(codeArray[4]);
      if (codeArray[5]) setInput6(codeArray[5]);

      if (codeArray.length >= 6) {
        document.getElementById("6").focus();
      } else if (codeArray.length > 0) {
        document.getElementById(Math.min(codeArray.length + 1, 6)).focus();
      }
    }
  };

  const handleCodeResend = async () => {
    const res = await Request(`/user/reset-password/code?email=${email}`);
    if (res.ok) {
      Swal.fire(
        "Code sent",
        "Please review your email and try again",
        "success"
      );
    }
  };

  const handleHiddenInputChange = (e) => {
    const value = e.target.value;
    if (/^\d+$/.test(value)) {
      const digits = value.split("").slice(0, 6);
      if (digits[0]) setInput1(digits[0]);
      if (digits[1]) setInput2(digits[1]);
      if (digits[2]) setInput3(digits[2]);
      if (digits[3]) setInput4(digits[3]);
      if (digits[4]) setInput5(digits[4]);
      if (digits[5]) setInput6(digits[5]);

      if (digits.length === 6) {
        document.getElementById("6").focus();
      } else if (digits.length > 0) {
        document.getElementById(Math.min(digits.length + 1, 6)).focus();
      }
    }
  };

  return (
    <div className="confirmation-code">
      <BallsBackground>
        <div className="reduced-content reduced-container shadow1">
          <div className="inner-content">
            <div className="content-wrapper">
              <div className="heading">
                <h1 className="title">Confirm email</h1>
                <p className="description">
                  We've sent a confirmation code to the email address you
                  provided. Please check your inbox to find the code and proceed
                  with your account verification. Make sure to check your Spam
                  or Junk Email folder, as sometimes emails can mistakenly end
                  up there.
                </p>
              </div>              <div className="confirmation-code-container">
                <label htmlFor="confirmationCode">Code</label>
                <p style={{ color: "#95B2C5", fontSize: "14px", margin: "0 0 10px 0" }}>
                  Puedes pegar el código completo en cualquiera de las casillas
                </p>
                <input
                  type="text"
                  style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  onChange={handleHiddenInputChange}
                  maxLength={6}
                  aria-hidden="true"
                />
                <div className="code">
                  <input
                    id="1"
                    type="number"
                    className="code-number"
                    name="input1"
                    value={input1}
                    onKeyDown={(e) => goBack(e, 1)}
                    onChange={(e) => setNext(e.target.value, 1)}
                    onPaste={(e) => handlePaste(e, 1)}
                  />
                  <input
                    id="2"
                    type="number"
                    className="code-number"
                    name="input2"
                    value={input2}
                    onKeyDown={(e) => goBack(e, 2)}
                    onChange={(e) => setNext(e.target.value, 2)}
                    onPaste={(e) => handlePaste(e, 2)}
                  />
                  <input
                    id="3"
                    type="number"
                    className="code-number"
                    name="input3"
                    value={input3}
                    onKeyDown={(e) => goBack(e, 3)}
                    onChange={(e) => setNext(e.target.value, 3)}
                    onPaste={(e) => handlePaste(e, 3)}
                  />
                  <input
                    id="4"
                    type="number"
                    className="code-number"
                    name="input4"
                    value={input4}
                    onKeyDown={(e) => goBack(e, 4)}
                    onChange={(e) => setNext(e.target.value, 4)}
                    onPaste={(e) => handlePaste(e, 4)}
                  />
                  <input
                    id="5"
                    type="number"
                    className="code-number"
                    name="input5"
                    value={input5}
                    onKeyDown={(e) => goBack(e, 5)}
                    onChange={(e) => setNext(e.target.value, 5)}
                    onPaste={(e) => handlePaste(e, 5)}
                  />
                  <input
                    id="6"
                    type="number"
                    className="code-number"
                    name="input6"
                    value={input6}
                    onKeyDown={(e) => goBack(e, 6)}
                    onChange={(e) => setNext(e.target.value, 6)}
                    onPaste={(e) => handlePaste(e, 6)}
                  />
                </div>
                <button
                  onClick={handleCodeResend}
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    color: "#95B2C5",
                    textDecoration: "underline",
                  }}
                >
                  Re-send
                </button>
              </div>
              <div className="button-container">
                {error && <div className="error">{error}</div>}
                <DgButton
                  category={"primary"}
                  size={"large"}
                  text={"Confirm"}
                  action={submit}
                  loading={loading}
                ></DgButton>
              </div>
            </div>
          </div>
        </div>
      </BallsBackground>
    </div>
  );
};

export default ConfirmationCode;
